import axios from "../axios";
import auth from "./auth";
import { bookingSource } from "../../../helper/visitSource";
import { bookingGrade } from "../../../helper/bookingGrade";

// 📅 Planning, côté élève et visiteur : créneaux libres d'une annonce (sans compte), prix et
// réservation d'un créneau (élève connecté).
// ⚠️ axios.get(chemin, x) du client du site ajoute x à l'adresse (« /slots/[object Object] ») :
// les paramètres passent par axios.query(chemin, { params }).
const withAuth = (request) =>
  new Promise((resolve, reject) => {
    request
      .then(({ data }) => resolve(data))
      .catch((err) => {
        reject(err);
        err?.response?.status === 401 && auth?.destroyToken();
      });
  });

const Planning = {
  getAdvertSlots(advertId, { duration, from, days } = {}) {
    return new Promise((resolve, reject) => {
      axios
        .query(`/adverts/${advertId}/slots`, { params: { duration, from, days } })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  },
  // Prix payé pour ce créneau (cours + frais élève, sans frais pour un Premium)
  quote(advertId, duration) {
    return withAuth(axios.query("/bookings/quote", { params: { advert_id: advertId, duration } }));
  },
  // 🔁 Série (2 à 4 cours, chaque semaine) : { count, duration, dates, unavailable: null | { date, reason } }
  seriesQuote(advertId, start, duration, count) {
    return withAuth(axios.query("/bookings/series/quote", { params: { advert_id: advertId, start, duration, count } }));
  },
  // 🔁 Série, un seul paiement : { status: true, series_id, dates } (porte-monnaie) ou
  // { redirection_link, series_id } (Stripe). ⚖️ withdrawalWaiver obligatoire
  // 🧭 source : d'où vient l'élève (statistiques du prof, tour 46), retenue par l'onglet
  // 🎓 grade : niveau choisi dans la recherche (carnet de suivi, suite du tour 58), retenu par l'onglet
  bookSeries({ advertId, start, duration, count, withdrawalWaiver = false }) {
    return withAuth(axios.post("/bookings/series", { advert_id: advertId, start, duration, count, withdrawal_waiver: withdrawalWaiver, source: bookingSource(advertId), grade: bookingGrade(advertId) }));
  },
  // 🔁 Lien de l'e-mail de renouvellement : { advert_id, start, duration, count, dates }
  seriesRenewal(seriesId) {
    return withAuth(axios.query(`/bookings/series/${seriesId}/renewal`));
  },
  // « Ne plus recevoir ces rappels » : lien signé, sans connexion
  seriesRemindersOff(userId, signature) {
    return new Promise((resolve, reject) => {
      axios
        .post(`/series-reminders/off/${userId}`, { signature })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });
  },
  // Réservation : { status: true } (payé avec le porte-monnaie) ou { redirection_link } (Stripe)
  // ⚖️ withdrawalWaiver : case du droit de rétractation cochée (obligatoire pour l'API)
  book({ advertId, start, duration, withdrawalWaiver = false }) {
    return withAuth(axios.post("/bookings", { advert_id: advertId, start, duration, withdrawal_waiver: withdrawalWaiver, source: bookingSource(advertId), grade: bookingGrade(advertId) }));
  },
  // Prochain cours réservé (prof ou élève) : { class: {...} | null }
  upcoming() {
    return withAuth(axios.query("/classes/upcoming"));
  },
  // Élève qui attend, prof en retard de plus de 15 min : annulation, remboursement complet
  cancelForTeacherLate(classId) {
    return withAuth(axios.post(`/classes/${classId}/cancel-teacher-late`));
  },
  // Prof : élève jamais venu, 15 min après l'heure → cours clos en « élève absent »
  markStudentAbsent(classId) {
    return withAuth(axios.post(`/classes/${classId}/student-no-show`));
  },
  // « Mes cours » > « Programmés » : { classes: [...] }
  scheduledClasses() {
    return withAuth(axios.query("/classes/scheduled"));
  },
  // Fenêtre d'annulation : règle, montants (élève) ou remboursement complet (prof)
  cancellationQuote(classId) {
    return withAuth(axios.query(`/classes/${classId}/cancellation`));
  },
  // Élève : { destination: "card" | "wallet", retained_cents (montant affiché) }
  cancelAsStudent(classId, { destination, retained_cents }) {
    return withAuth(axios.post(`/classes/${classId}/cancel`, { destination, retained_cents }));
  },
  // Prof : motif obligatoire
  cancelAsTeacher(classId, reason) {
    return withAuth(axios.post(`/classes/${classId}/teacher-cancel`, { reason }));
  },
  // 🔁 Série : cours restants et montants (élève) ou remboursement complet (prof)
  seriesCancellationQuote(seriesId) {
    return withAuth(axios.query(`/series/${seriesId}/cancellation`));
  },
  // Élève : « Annuler les cours restants », { destination, retained_cents (total affiché) }
  cancelSeriesAsStudent(seriesId, { destination, retained_cents }) {
    return withAuth(axios.post(`/series/${seriesId}/cancel`, { destination, retained_cents }));
  },
  // Prof : toute la série, motif obligatoire
  cancelSeriesAsTeacher(seriesId, reason) {
    return withAuth(axios.post(`/series/${seriesId}/teacher-cancel`, { reason }));
  },
  // Rappels par e-mail (la veille et 1 h avant) : { enabled }
  reminders() {
    return withAuth(axios.query("/classes/reminders"));
  },
  setReminders(enabled) {
    return withAuth(axios.put("/classes/reminders", { enabled }));
  },
};

export default Planning;
